// Hooks
import { useBasicPaginatedResource } from "@Common/hooks/Pagination/useBasicPaginatedResource";

// Utils
import { and } from "@Common/helpers/sonar";
import { getAuthorizationGroupsAccess } from "@Settings/api/GroupAccessSettings/queries";

// Types
import { IPageParam } from "@/Common/api/types/pagination";
import { ILocation } from "@Common/interfaces/location";

const PAGE_SIZE = 20;

export const useAuthorizationGroupsAccessPaginatedQuery = ({ location }: { location: ILocation }) => {
	const locationId = location?.locationId;

	const { data, ...otherResults } = useBasicPaginatedResource({
		queryKey: ["GroupSettingAccess", locationId],
		queryFn: async ({ pageParam }) => {
			const typedPageParam = pageParam as IPageParam<string>;
			if (!typedPageParam || !("after" in typedPageParam) || !("first" in typedPageParam)) {
				throw new Error("PageParam should have properties 'after' and 'first'");
			}

			const userGroups = await getAuthorizationGroupsAccess(locationId, {
				first: typedPageParam.first,
				after: typedPageParam.after,
			});

			if (!userGroups) {
				return {
					items: [],
					pageInfo: {
						hasNextPage: false,
						endCursor: "",
					},
				};
			}

			return userGroups;
		},

		getNextPageParam: (lastPage) => {
			const { pageInfo } = lastPage ?? {};
			return pageInfo?.hasNextPage ? { first: PAGE_SIZE, after: pageInfo.endCursor ?? undefined } : undefined;
		},
		initialPageParam: {
			first: PAGE_SIZE,
			after: "",
		},
		fetchAllPages: true,
		enabled: and(Boolean(locationId)),
		staleTime: Infinity,
	});

	return {
		data,
		...otherResults,
	};
};
